// Builds a single, combined docx `Document` from an AssembledContract:
// master cover -> contents -> [section cover -> section body] per part.
// Minimalist, formal styling. No browser/Node-specific APIs, so it is shared by
// the in-browser exporter (Packer.toBlob) and the Node emitter (Packer.toBuffer).

import {
  AlignmentType,
  BorderStyle,
  Document,
  Footer,
  Header,
  HeadingLevel,
  ImageRun,
  PageBreak,
  PageNumber,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TextRun,
  WidthType,
} from "docx";
import type { AssembledContract, Block, ContractPart, CoverSpec, PaperSize, SignParty } from "../contracts/types";
import { signatureBox } from "../contracts/imageSize";

const INK = "1A1A1A";
const MUTE = "6B7280";
const RULE = "1A1A1A";
const LINE = "C9CDD4";
// All document text is sans-serif for a consistent, clean look.
const SERIF = "Calibri";
const SANS = "Calibri";
// Page geometry (twips) so every section is the same paper size.
const PAGE_A4 = { width: 11906, height: 16838 };
const PAGE_LETTER = { width: 12240, height: 15840 };

function hairline(color = RULE, size = 6): Paragraph {
  return new Paragraph({ spacing: { before: 80, after: 80 }, border: { bottom: { style: BorderStyle.SINGLE, size, color, space: 1 } }, children: [] });
}

// --- Cover page --------------------------------------------------------------
function coverPage(spec: CoverSpec, isMaster: boolean): (Paragraph | Table)[] {
  const out: (Paragraph | Table)[] = [];
  out.push(new Paragraph({ spacing: { before: isMaster ? 2600 : 1800 }, children: [] }));
  if (spec.kicker) {
    out.push(new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 200 }, children: [new TextRun({ text: spec.kicker.toUpperCase(), font: SANS, size: 20, color: MUTE, characterSpacing: 80 })] }));
  }
  out.push(new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 160 }, children: [new TextRun({ text: spec.title, font: SERIF, size: isMaster ? 60 : 44, color: INK })] }));
  // Centered short rule under the title
  out.push(new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 160 }, border: { bottom: { style: BorderStyle.SINGLE, size: 8, color: RULE, space: 1 } }, indent: { left: 3200, right: 3200 }, children: [] }));
  if (spec.subtitle) {
    out.push(new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 200 }, indent: { left: 1200, right: 1200 }, children: [new TextRun({ text: spec.subtitle, font: SERIF, italics: true, size: 24, color: MUTE })] }));
  }
  const cline = (text: string, size: number, color = INK, caps = false) =>
    new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 60 }, children: [new TextRun({ text, font: SANS, size, color, characterSpacing: caps ? 40 : 0 })] });
  if (spec.contractLabel || spec.standardLine || spec.forParty || spec.preparedBy?.length) {
    out.push(new Paragraph({ spacing: { before: 500 }, children: [] }));
    if (spec.contractLabel) out.push(cline(spec.contractLabel.toUpperCase(), 26, INK, true));
    if (spec.standardLine) out.push(cline(spec.standardLine, 20, MUTE, true));
    if (spec.date) out.push(cline(spec.date, 20, MUTE));
    if (spec.forParty) {
      out.push(new Paragraph({ spacing: { before: 380 }, children: [] }));
      out.push(cline("FOR", 16, MUTE, true));
      out.push(cline(spec.forParty, 24, INK));
    }
    if (spec.preparedBy?.length) {
      out.push(new Paragraph({ spacing: { before: 320 }, children: [] }));
      out.push(cline("PREPARED BY", 16, MUTE, true));
      spec.preparedBy.forEach((l, i) => out.push(cline(l, i === 0 ? 22 : 18, i === 0 ? INK : MUTE)));
    }
    if (spec.projectNo) {
      out.push(new Paragraph({ spacing: { before: 360 }, children: [] }));
      out.push(cline(spec.projectNo, 18, MUTE, true));
    }
  }
  if (spec.meta?.length) {
    out.push(new Paragraph({ spacing: { before: isMaster ? 2600 : 1600 }, children: [] }));
    const rows = spec.meta.map(
      (m) =>
        new TableRow({
          children: [
            new TableCell({ width: { size: 38, type: WidthType.PERCENTAGE }, margins: { top: 40, bottom: 40 }, borders: noBorders(), children: [new Paragraph({ children: [new TextRun({ text: m.label.toUpperCase(), font: SANS, size: 16, color: MUTE, characterSpacing: 40 })] })] }),
            new TableCell({ width: { size: 62, type: WidthType.PERCENTAGE }, margins: { top: 40, bottom: 40 }, borders: noBorders(), children: [new Paragraph({ children: [new TextRun({ text: m.value, font: SERIF, size: 20, color: INK })] })] }),
          ],
        })
    );
    out.push(new Table({ width: { size: 78, type: WidthType.PERCENTAGE }, alignment: AlignmentType.CENTER, borders: noBorders(), rows }));
  }
  out.push(new Paragraph({ children: [new PageBreak()] }));
  return out;
}

function noBorders() {
  const none = { style: BorderStyle.NONE, size: 0, color: "FFFFFF" } as const;
  return { top: none, bottom: none, left: none, right: none, insideHorizontal: none, insideVertical: none };
}

// --- Contents page -----------------------------------------------------------
function contentsPage(parts: ContractPart[]): (Paragraph | Table)[] {
  const out: (Paragraph | Table)[] = [];
  out.push(new Paragraph({ spacing: { before: 400, after: 60 }, children: [new TextRun({ text: "Contents", font: SERIF, size: 40, color: INK })] }));
  out.push(hairline());
  parts.forEach((p, i) => {
    out.push(
      new Paragraph({
        spacing: { before: 140, after: 40 },
        tabStops: [{ type: "right" as any, position: 9000, leader: "dot" as any }],
        children: [new TextRun({ text: `${i + 1}.  ${p.name}`, font: SERIF, size: 22, color: INK })],
      })
    );
  });
  out.push(new Paragraph({ children: [new PageBreak()] }));
  return out;
}

// --- Block renderers ---------------------------------------------------------
const HMAP = [HeadingLevel.HEADING_1, HeadingLevel.HEADING_2, HeadingLevel.HEADING_3];

function renderBlock(bl: Block): (Paragraph | Table)[] {
  switch (bl.type) {
    case "heading":
      return [
        new Paragraph({
          heading: HMAP[Math.min(bl.level, 3) - 1],
          spacing: { before: bl.level === 1 ? 240 : 200, after: 100 },
          border: bl.level === 1 ? { bottom: { style: BorderStyle.SINGLE, size: 4, color: LINE, space: 2 } } : undefined,
          children: [new TextRun({ text: bl.text, font: SERIF, bold: true, size: bl.level === 1 ? 30 : bl.level === 2 ? 24 : 22, color: INK })],
        }),
      ];
    case "paragraph":
      return [new Paragraph({ spacing: { after: 120, line: 276 }, alignment: bl.align === "left" ? AlignmentType.LEFT : AlignmentType.JUSTIFIED, children: [new TextRun({ text: bl.text, font: SERIF, size: 21, color: INK })] })];
    case "clause":
      return [new Paragraph({ spacing: { after: 80, line: 276 }, indent: { left: bl.text.startsWith("     ") ? 480 : 0 }, children: [new TextRun({ text: bl.text.trim(), font: SERIF, size: 21, color: INK })] })];
    case "list":
      return bl.items.map((it) => {
        const lvl = it.level || 0;
        const prefix = it.prefix ? it.prefix + "  " : "";
        return new Paragraph({
          spacing: { after: 70, line: 268 },
          indent: { left: 360 + lvl * 360, hanging: 260 },
          children: [new TextRun({ text: prefix, font: SERIF, size: 21, color: INK }), new TextRun({ text: it.text, font: SERIF, size: 21, color: INK })],
        });
      });
    case "table":
      return [renderTable(bl.columns, bl.rows)];
    case "signblock":
      return renderSignBlock(bl.parties);
    case "spacer":
      return [new Paragraph({ spacing: { after: 200 }, children: [] })];
    case "pagebreak":
      return [new Paragraph({ children: [new PageBreak()] })];
  }
}

function renderTable(columns: string[], rows: string[][]): Table {
  const colW = Math.floor(100 / columns.length);
  const cell = (text: string, header: boolean) =>
    new TableCell({
      width: { size: colW, type: WidthType.PERCENTAGE },
      shading: header ? { fill: "F2F3F5" } : undefined,
      margins: { top: 60, bottom: 60, left: 100, right: 100 },
      children: [new Paragraph({ children: [new TextRun({ text, font: SANS, size: 18, bold: header, color: header ? INK : "222222" })] })],
    });
  const border = { style: BorderStyle.SINGLE, size: 2, color: LINE } as const;
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: { top: border, bottom: border, left: border, right: border, insideHorizontal: border, insideVertical: border },
    rows: [new TableRow({ tableHeader: true, cantSplit: true, children: columns.map((c) => cell(c, true)) }), ...rows.map((r) => new TableRow({ cantSplit: true, children: r.map((v) => cell(v, false)) }))],
  });
}

/** Decode a data-URI signature into a docx ImageRun (best-effort). */
function sigImageRun(dataUri?: string): ImageRun | null {
  if (!dataUri) return null;
  try {
    const m = /^data:image\/(png|jpe?g);base64,(.+)$/i.exec(dataUri);
    if (!m) return null;
    const type = m[1].toLowerCase().startsWith("jp") ? "jpg" : "png";
    const bytes = Uint8Array.from(atob(m[2]), (c) => c.charCodeAt(0));
    const box = signatureBox(dataUri, 185, 58); // aspect-correct, bounded
    return new ImageRun({ data: bytes, type: type as "png" | "jpg", transformation: { width: box.width, height: box.height } });
  } catch {
    return null;
  }
}

function renderSignBlock(parties: SignParty[]): (Paragraph | Table)[] {
  const out: (Paragraph | Table)[] = [];
  out.push(new Paragraph({ spacing: { before: 320, after: 120 }, children: [new TextRun({ text: "Execution", font: SANS, size: 18, bold: true, color: MUTE, characterSpacing: 40 })] }));

  const line = (label: string) => new Paragraph({ spacing: { before: 260, after: 20 }, children: [new TextRun({ text: label, font: SERIF, size: 20, color: INK })] });
  const ruleFor = () => new Paragraph({ spacing: { after: 40 }, border: { bottom: { style: BorderStyle.SINGLE, size: 4, color: INK, space: 1 } }, indent: { right: 400 }, children: [] });

  const col = (p: SignParty): TableCell => {
    const kids: Paragraph[] = [];
    if (p.caption) kids.push(new Paragraph({ spacing: { after: 30 }, children: [new TextRun({ text: p.caption, font: SANS, size: 16, color: MUTE })] }));
    kids.push(new Paragraph({ spacing: { after: 60 }, children: [new TextRun({ text: `Signed for and on behalf of the ${p.role}`, font: SANS, size: 18, bold: true, color: INK })] }));
    kids.push(line("Name:"));
    kids.push(new Paragraph({ children: [new TextRun({ text: p.name || "____________________", font: SERIF, size: 20, color: INK })] }));
    kids.push(line("Title:"));
    kids.push(new Paragraph({ children: [new TextRun({ text: p.title || "____________________", font: SERIF, size: 20, color: INK })] }));
    kids.push(line("Signature:"));
    const sig = sigImageRun(p.signatureDataUri);
    if (sig) kids.push(new Paragraph({ spacing: { before: 40, after: 20 }, children: [sig] }));
    kids.push(ruleFor());
    kids.push(line("Date:"));
    if (p.signedDate) kids.push(new Paragraph({ children: [new TextRun({ text: p.signedDate, font: SERIF, size: 20, color: INK })] }));
    kids.push(ruleFor());
    if (p.witnessName !== undefined || p.witnessAddress !== undefined) {
      kids.push(new Paragraph({ spacing: { before: 200 }, children: [new TextRun({ text: "In the presence of (Witness)", font: SANS, size: 16, color: MUTE })] }));
      kids.push(line("Name:"));
      kids.push(new Paragraph({ children: [new TextRun({ text: p.witnessName || "____________________", font: SERIF, size: 20, color: INK })] }));
      kids.push(line("Address:"));
      kids.push(new Paragraph({ children: [new TextRun({ text: p.witnessAddress || "____________________", font: SERIF, size: 20, color: INK })] }));
    }
    return new TableCell({ width: { size: 50, type: WidthType.PERCENTAGE }, margins: { top: 80, bottom: 80, left: 40, right: 240 }, borders: noBorders(), children: kids });
  };

  if (parties.length === 1) {
    out.push(new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, borders: noBorders(), rows: [new TableRow({ children: [col(parties[0]), new TableCell({ width: { size: 50, type: WidthType.PERCENTAGE }, borders: noBorders(), children: [new Paragraph({ children: [] })] })] })] }));
  } else {
    // Two per row
    for (let i = 0; i < parties.length; i += 2) {
      const pair = parties.slice(i, i + 2);
      const cells = pair.map(col);
      if (cells.length === 1) cells.push(new TableCell({ width: { size: 50, type: WidthType.PERCENTAGE }, borders: noBorders(), children: [new Paragraph({ children: [] })] }));
      out.push(new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, borders: noBorders(), rows: [new TableRow({ children: cells })] }));
    }
  }
  return out;
}

// --- Document ----------------------------------------------------------------
export function buildDocument(contract: AssembledContract, size: PaperSize = "A4"): Document {
  const pageSize = size === "LETTER" ? PAGE_LETTER : PAGE_A4;
  const children: (Paragraph | Table)[] = [];

  // Short single-part documents (payment certificates, IPCs) skip the Contents
  // page and the redundant part cover — cover, then the certificate itself.
  const single = contract.parts.length === 1;
  if (!contract.noCover) children.push(...coverPage(contract.masterCover, true));
  if (!single) children.push(...contentsPage(contract.parts));

  contract.parts.forEach((part, i) => {
    if (!single) children.push(...coverPage(part.cover, false));
    for (const bl of part.blocks) children.push(...renderBlock(bl));
    // A letter's closing notice belongs under the signature block, not alone on
    // a second page. Contracts keep the trailing break so the notice ends the
    // document on its own page.
    if (!contract.noCover || i < contract.parts.length - 1) children.push(new Paragraph({ children: [new PageBreak()] }));
  });

  // Closing notice
  children.push(hairline(LINE, 4));
  children.push(new Paragraph({ spacing: { before: 120 }, children: [new TextRun({ text: contract.notice, font: SERIF, italics: true, size: 16, color: MUTE })] }));

  return new Document({
    creator: "Studio Contract",
    title: contract.title,
    styles: { default: { document: { run: { font: SERIF, size: 21, color: INK } } } },
    sections: [
      {
        properties: { page: { size: pageSize, margin: { top: 1440, bottom: 1440, left: 1440, right: 1440 } } },
        headers: {
          default: new Header({
            children: [new Paragraph({ alignment: AlignmentType.RIGHT, spacing: { after: 40 }, border: { bottom: { style: BorderStyle.SINGLE, size: 3, color: LINE, space: 4 } }, children: [new TextRun({ text: `${contract.title}  ·  ${contract.standardLabel.split("—")[0].trim()}`, font: SANS, size: 15, color: MUTE })] })],
          }),
        },
        footers: {
          default: new Footer({
            children: [
              new Paragraph({
                alignment: AlignmentType.CENTER,
                border: { top: { style: BorderStyle.SINGLE, size: 3, color: LINE, space: 4 } },
                children: [new TextRun({ children: ["Page ", PageNumber.CURRENT, " of ", PageNumber.TOTAL_PAGES], font: SANS, size: 15, color: MUTE })],
              }),
            ],
          }),
        },
        children,
      },
    ],
  });
}
